import { useCallback, useMemo } from 'react';

import { useActorRef, useSelector } from '@xstate/react';
import { type ContextFrom, fromPromise, type SnapshotFrom } from 'xstate';

import { createOrdersMachine } from '../../orders-machine';
import {
  type FetchOrdersOutput,
  type OrdersMachineContext,
  type OrderWithIdAndWantedTime,
} from '../../orders-machine.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook to provide an interface to communicate with the Orders state machine.
 */
export const useOrders = <Order extends OrderWithIdAndWantedTime>(
  params: UseReorderOrdersParams<Order>,
) => {
  const { fetchOrders, ordersPerPage = 10 } = params;

  // ─── State Machine ───────────────────────────────────────────────────

  const reorderOrdersMachine = useMemo(() => createOrdersMachine<Order>('waiting'), []);

  const fetchOrdersActor = useMemo(
    () =>
      fromPromise<FetchOrdersOutput<Order>, OrdersMachineContext<Order>>(async ({ input }) =>
        fetchOrders(input),
      ),
    [fetchOrders],
  );

  // ─── Machine Actor ───────────────────────────────────────────────────

  const actorRef = useActorRef(
    reorderOrdersMachine.provide({ actors: { fetchOrders: fetchOrdersActor } }),
    { input: { ordersPerPage } },
  );

  // ─── Context ─────────────────────────────────────────────────────────

  const orders = useSelector(actorRef, (snapshot) => snapshot.context.orders);

  const canFetchMoreOrders = useSelector(
    actorRef,
    (snapshot) => snapshot.context.canFetchMoreOrders,
  );

  const mostRecentOrder = orders[0];

  // ─── Flags ───────────────────────────────────────────────────────────

  const isFetchingInitialOrders = useSelector(actorRef, checkIfFetchingInitialOrders);
  const isReFetchingOrders = useSelector(actorRef, checkIfReFetchingOrders);
  const isFetchingMoreOrders = useSelector(actorRef, checkIfFetchingMoreOrders);

  // ─── Machine Helpers ─────────────────────────────────────────────────

  const fetchInitialOrders = useCallback(() => {
    actorRef.send({ type: 'START' });
  }, [actorRef]);

  const fetchMoreOrders = useCallback(() => {
    actorRef.send({ type: 'FETCH_MORE_ORDERS' });
  }, [actorRef]);

  const refetchOrders = useCallback(() => {
    actorRef.send({ type: 'REFETCH_ORDERS' });
  }, [actorRef]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    orders,
    mostRecentOrder,
    isFetchingInitialOrders,
    isFetchingMoreOrders,
    isReFetchingOrders,
    fetchInitialOrders,
    fetchMoreOrders,
    refetchOrders,
    canFetchMoreOrders,
  };
};

// ─── Selectors ───────────────────────────────────────────────────────────────

function checkIfFetchingInitialOrders<Order extends OrderWithIdAndWantedTime>(
  snapshot: OrdersMachineSnapshot<Order>,
) {
  return snapshot.matches('waiting') || snapshot.matches('fetchingInitialOrders');
}

function checkIfReFetchingOrders<Order extends OrderWithIdAndWantedTime>(
  snapshot: OrdersMachineSnapshot<Order>,
) {
  return snapshot.matches('refetchingOrders');
}

function checkIfFetchingMoreOrders<Order extends OrderWithIdAndWantedTime>(
  snapshot: OrdersMachineSnapshot<Order>,
) {
  return snapshot.matches('fetchingMoreOrders');
}

// ─── Types ───────────────────────────────────────────────────────────────────

type OrdersMachine<Order extends OrderWithIdAndWantedTime> = ReturnType<
  typeof createOrdersMachine<Order>
>;

type OrdersMachineSnapshot<Order extends OrderWithIdAndWantedTime> = SnapshotFrom<
  OrdersMachine<Order>
>;

type UseReorderOrdersParams<Order extends OrderWithIdAndWantedTime> = {
  fetchOrders: (context: ContextFrom<OrdersMachine<Order>>) => Promise<FetchOrdersOutput<Order>>;
  ordersPerPage?: number;
};
